import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { useEffect, useState } from "react"
import { api } from "../../../../convex/_generated/api"
import type { Id } from "../../../../convex/_generated/dataModel"
import { gameRegistry } from "@/games/registry"
import { ALL_DECADES, lcConfigSchema } from "@/games/lyrically-challenged/schemas"
import { analyticsEvents } from "@/lib/analytics/events"
import { buildLyricallyChallengedEventNamePreview } from "@/lib/eventNames"
import { trackEvent } from "@/lib/analytics/posthog"
import { VenueField, useVenueResolver } from "@/components/VenueField"
import { useWildGuessSoloEnabled } from "@/lib/analytics/posthog"

export const Route = createFileRoute("/_authed/host/events/$eventId")({
  component: EditEvent,
})

type SubmitState = "idle" | "submitting" | "saved"
type EventStatus = "scheduled" | "ready" | "active" | "completed" | "cancelled"

const EVENT_STAGES: ReadonlyArray<{
  status: EventStatus
  label: string
  detail: string
}> = [
  {
    status: "scheduled",
    label: "Scheduled",
    detail: "Visible as upcoming. Team registration is closed.",
  },
  {
    status: "ready",
    label: "Ready",
    detail: "Take seats, create teams, and share team codes.",
  },
  {
    status: "active",
    label: "Active",
    detail: "The room is live. Run individual games from the game list.",
  },
  {
    status: "completed",
    label: "Completed",
    detail: "The event is wrapped.",
  },
]

function EditEvent() {
  const { eventId } = Route.useParams()
  const event = useQuery(api.events.getById, {
    eventId: eventId as Id<"events">,
  })
  const eventGames = useQuery(api.games.listForEvent, {
    eventId: eventId as Id<"events">,
  })
  const update = useMutation(api.events.update)
  const softDelete = useMutation(api.events.softDelete)
  const { resolve: resolveVenue } = useVenueResolver()
  const venue = useQuery(
    api.venues.listAll,
    event?.venueId ? undefined : "skip",
  )
  const navigate = useNavigate()
  const reduce = useReducedMotion()

  const [startsAt, setStartsAt] = useState("")
  const [venueName, setVenueName] = useState("")
  const [status, setStatus] = useState<EventStatus>("scheduled")
  const [hydrated, setHydrated] = useState(false)
  const [submitState, setSubmitState] = useState<SubmitState>("idle")
  const [statusChanging, setStatusChanging] = useState<EventStatus | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (event && venue !== undefined && !hydrated) {
      setStartsAt(toDatetimeLocal(event.startsAt))
      setStatus(event.status)
      const v = event.venueId
        ? venue?.find((x) => x._id === event.venueId)
        : undefined
      setVenueName(v?.name ?? "")
      setHydrated(true)
    }
  }, [event, venue, hydrated])

  // Reset the "Saved" pulse back to idle so subsequent edits can show it again.
  useEffect(() => {
    if (submitState !== "saved") return
    const t = setTimeout(() => setSubmitState("idle"), 1400)
    return () => clearTimeout(t)
  }, [submitState])

  if (event === undefined) {
    return <FormSkeleton />
  }
  if (event === null) {
    return (
      <div className="rounded-md border border-dashed border-[color:var(--border)] bg-[color:var(--muted)]/30 p-8 text-center">
        <p className="text-sm font-medium">Event not found</p>
        <p className="mt-1 text-xs text-[color:var(--muted-foreground)]">
          It may have been deleted, or the link is wrong.
        </p>
        <Link
          to="/host/events"
          className="mt-4 inline-flex items-center gap-1 rounded-md border border-[color:var(--border)] px-3 py-1.5 text-xs hover:bg-[color:var(--muted)]"
        >
          ← Back to events
        </Link>
      </div>
    )
  }

  const eventLabel = buildLyricallyChallengedEventNamePreview(
    venueName,
    startsAt,
  )
  const runGame =
    eventGames?.find(
      (game) => game.status !== "completed" && game.status !== "cancelled",
    ) ??
    eventGames?.[0] ??
    null

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const startsAtMs = new Date(startsAt).getTime()
    if (!Number.isFinite(startsAtMs)) {
      setError("Pick a real date and time.")
      return
    }
    setSubmitState("submitting")
    setError(null)
    try {
      const venueId = await resolveVenue(venueName)
      await update({
        eventId: eventId as Id<"events">,
        name: eventLabel,
        startsAt: startsAtMs,
        venueId,
        status,
      })
      trackEvent(analyticsEvents.hostEventUpdated, {
        eventId,
        status,
        hasVenue: Boolean(venueId),
      })
      setSubmitState("saved")
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setSubmitState("idle")
    }
  }

  async function handleStatusChange(nextStatus: EventStatus) {
    if (nextStatus === status) return
    setStatusChanging(nextStatus)
    setError(null)
    try {
      await update({
        eventId: eventId as Id<"events">,
        status: nextStatus,
      })
      trackEvent(analyticsEvents.hostEventUpdated, {
        eventId,
        status: nextStatus,
        changeType: "lifecycle",
      })
      setStatus(nextStatus)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setStatusChanging(null)
    }
  }

  async function handleDelete() {
    if (
      !confirm(
        `Delete "${eventLabel}"? Games and history attached to this event are kept (it's a soft delete) but it'll disappear from your list.`,
      )
    )
      return
    try {
      await softDelete({ eventId: eventId as Id<"events"> })
      trackEvent(analyticsEvents.hostEventDeleted, { eventId })
      navigate({ to: "/host/events" })
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <Link
          to="/host/events"
          className="rounded-sm text-xs text-[color:var(--muted-foreground)] transition-colors hover:text-[color:var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          ← All events
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">
            Event setup
          </h1>
          <StatusPill status={status} />
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--muted)]/20 p-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--muted-foreground)]">
            Event label
          </p>
          <p className="mt-2 text-base font-semibold text-[color:var(--foreground)]">
            {eventLabel}
          </p>
          <p className="mt-1 text-xs leading-5 text-[color:var(--muted-foreground)]">
            Generated from Lyrically Challenged, venue, and date.
          </p>
        </div>

        <EventLifecyclePanel
          status={status}
          busyStatus={statusChanging}
          runGameId={runGame?._id ?? null}
          onChange={(nextStatus) => void handleStatusChange(nextStatus)}
        />

        <Field label="Starts at">
          <input
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            required
            className="w-full rounded-md border border-[color:var(--border)] bg-[color:var(--background)] px-3 py-2 text-sm transition-colors duration-150 focus-visible:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          />
        </Field>

        <VenueField value={venueName} onChange={setVenueName} />

        <AnimatePresence>
          {error && (
            <motion.p
              key="err"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              role="alert"
              className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-900"
            >
              {error}
            </motion.p>
          )}
        </AnimatePresence>

        <div className="flex flex-wrap items-center gap-2">
          <SubmitButton state={submitState} idleLabel="Save" reduce={reduce} />
          <Link
            to="/host/events"
            className="rounded-md border border-[color:var(--border)] px-4 py-2 text-sm transition-colors duration-150 hover:bg-[color:var(--muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
          >
            Cancel
          </Link>
          <div className="flex-1" />
          <button
            type="button"
            onClick={handleDelete}
            className="rounded-md border border-red-300 px-3 py-2 text-xs text-red-700 transition-colors duration-150 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300/40 focus-visible:ring-offset-2"
          >
            Delete event
          </button>
        </div>
      </form>

      <GamesSection
        eventId={eventId as Id<"events">}
        eventStatus={status}
        onEventReopened={() => setStatus("ready")}
      />
    </div>
  )
}

function EventLifecyclePanel({
  status,
  busyStatus,
  runGameId,
  onChange,
}: {
  status: EventStatus
  busyStatus: EventStatus | null
  runGameId: Id<"games"> | null
  onChange: (status: EventStatus) => void
}) {
  const activeIndex = EVENT_STAGES.findIndex((stage) => stage.status === status)
  const nextAction =
    status === "scheduled"
      ? { status: "ready" as const, label: "Open registration" }
      : null
  const showRunHandoff = status === "ready" || status === "active"

  return (
    <section className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--muted-foreground)]">
            Event lifecycle
          </p>
          <h2 className="mt-1 text-base font-semibold text-[color:var(--foreground)]">
            {EVENT_STAGES.find((stage) => stage.status === status)?.label ??
              "Canceled"}
          </h2>
          <p className="mt-1 text-xs leading-5 text-[color:var(--muted-foreground)]">
            {status === "cancelled"
              ? "This event is canceled and closed to players."
              : EVENT_STAGES.find((stage) => stage.status === status)?.detail}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {nextAction && (
            <button
              type="button"
              onClick={() => onChange(nextAction.status)}
              disabled={busyStatus !== null}
              className="rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground shadow-[0_14px_32px_-22px_rgba(67,65,201,0.9)] transition-all duration-150 hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {busyStatus === nextAction.status ? "Updating…" : nextAction.label}
            </button>
          )}
          {showRunHandoff && runGameId && (
            <Link
              to="/host/games/$gameId"
              params={{ gameId: runGameId }}
              className="rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground shadow-[0_14px_32px_-22px_rgba(67,65,201,0.9)] transition-all duration-150 hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 active:scale-[0.98]"
            >
              Event running
            </Link>
          )}
          {status !== "completed" && status !== "cancelled" && (
            <button
              type="button"
              onClick={() => onChange("cancelled")}
              disabled={busyStatus !== null}
              className="rounded-full border border-red-300 px-3 py-2 text-xs font-semibold text-red-700 transition-colors duration-150 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300/40 focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {busyStatus === "cancelled" ? "Canceling…" : "Cancel event"}
            </button>
          )}
        </div>
      </div>
      <ol className="mt-4 grid gap-2 sm:grid-cols-4">
        {EVENT_STAGES.map((stage, index) => {
          const isActive = stage.status === status
          const isPast = activeIndex > index && status !== "cancelled"
          return (
            <li
              key={stage.status}
              className={[
                "rounded-lg border px-3 py-2",
                isActive
                  ? "border-primary bg-primary/10 text-[color:var(--foreground)]"
                  : isPast
                    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                    : "border-[color:var(--border)] bg-[color:var(--background)] text-[color:var(--muted-foreground)]",
              ].join(" ")}
            >
              <p className="text-xs font-bold">{stage.label}</p>
              <p className="mt-1 text-[11px] leading-4">{stage.detail}</p>
            </li>
          )
        })}
      </ol>
    </section>
  )
}

function SubmitButton({
  state,
  idleLabel,
  reduce,
}: {
  state: SubmitState
  idleLabel: string
  reduce: boolean | null
}) {
  const disabled = state === "submitting"
  const label =
    state === "submitting"
      ? "Saving…"
      : state === "saved"
        ? "Saved"
        : idleLabel

  return (
    <button
      type="submit"
      disabled={disabled}
      aria-live="polite"
      className={`relative inline-flex min-w-[6.5rem] items-center justify-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium shadow-sm transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 ${
        state === "saved"
          ? "bg-emerald-600 text-white"
          : "bg-primary text-primary-foreground hover:shadow-md hover:brightness-105 active:scale-[0.98]"
      } disabled:cursor-default ${state === "submitting" ? "opacity-80" : ""}`}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={state}
          initial={{ opacity: 0, y: reduce ? 0 : 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: reduce ? 0 : -4 }}
          transition={{ duration: 0.16, ease: "easeOut" }}
          className="inline-flex items-center gap-1.5"
        >
          {state === "saved" && (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M5 13l4 4L19 7" />
            </svg>
          )}
          {state === "submitting" && (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              className="animate-spin"
              aria-hidden
            >
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                strokeOpacity="0.25"
                strokeWidth="3"
              />
              <path
                d="M21 12a9 9 0 0 0-9-9"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          )}
          {label}
        </motion.span>
      </AnimatePresence>
    </button>
  )
}

function StatusPill({ status }: { status: EventStatus }) {
  const reduce = useReducedMotion()
  const tones: Record<EventStatus, { cls: string; dot: string }> = {
    scheduled: {
      cls: "border-[color:var(--border)] bg-[color:var(--background)] text-[color:var(--muted-foreground)]",
      dot: "bg-[color:var(--muted-foreground)]",
    },
    ready: {
      cls: "border-amber-300 bg-amber-50 text-amber-800",
      dot: "bg-amber-500",
    },
    active: {
      cls: "border-emerald-300 bg-emerald-50 text-emerald-800",
      dot: "bg-emerald-500",
    },
    completed: {
      cls: "border-[color:var(--border)] bg-[color:var(--muted)] text-[color:var(--muted-foreground)]",
      dot: "bg-[color:var(--muted-foreground)]",
    },
    cancelled: {
      cls: "border-red-200 bg-red-50 text-red-700",
      dot: "bg-red-400",
    },
  }
  const tone = tones[status]
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.span
        key={status}
        initial={{ opacity: 0, y: reduce ? 0 : -2, scale: reduce ? 1 : 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: reduce ? 0 : 2, scale: reduce ? 1 : 0.98 }}
        transition={{ duration: 0.18, ease: "easeOut" }}
        className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wide ${tone.cls}`}
      >
        <span
          className={`inline-block h-1.5 w-1.5 rounded-full ${tone.dot} ${
            status === "active" && !reduce ? "animate-pulse" : ""
          }`}
          aria-hidden
        />
        {status === "cancelled" ? "canceled" : status}
      </motion.span>
    </AnimatePresence>
  )
}

function FormSkeleton() {
  return (
    <div className="max-w-2xl space-y-8">
      <div className="space-y-3">
        <div className="h-3 w-24 animate-pulse rounded bg-[color:var(--muted)]" />
        <div className="h-7 w-48 animate-pulse rounded bg-[color:var(--muted)]" />
      </div>
      <div className="space-y-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="space-y-2">
            <div className="h-3 w-20 animate-pulse rounded bg-[color:var(--muted)]" />
            <div className="h-9 w-full animate-pulse rounded-md bg-[color:var(--muted)]" />
          </div>
        ))}
      </div>
    </div>
  )
}

function GamesSection({
  eventId,
  eventStatus,
  onEventReopened,
}: {
  eventId: Id<"events">
  eventStatus: EventStatus
  onEventReopened?: () => void
}) {
  const games = useQuery(api.games.listForEvent, { eventId })
  const createGame = useMutation(api.games.create)
  const navigate = useNavigate()
  const reduce = useReducedMotion()
  const soloEnabled = useWildGuessSoloEnabled()
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const visibleGames = games?.filter(
    (game) => soloEnabled || game.gameType !== "solo-trivia",
  )
  const canCreateGame = eventStatus !== "cancelled"
  const eventCompleted = eventStatus === "completed"
  const activeOrQueuedGames = visibleGames?.filter(
    (game) => game.status !== "completed" && game.status !== "cancelled",
  )
  const hasGames = (visibleGames?.length ?? 0) > 0
  const createLabel =
    eventCompleted || (hasGames && activeOrQueuedGames?.length === 0)
      ? "Create next game"
      : "Add Lyrically Challenged"

  async function handleCreateLC() {
    if (!canCreateGame) return
    setCreating(true)
    setError(null)
    try {
      const reopensEvent = eventStatus === "completed"
      const config = lcConfigSchema.parse({
        decadesEnabled: ALL_DECADES,
      })
      const gameId = await createGame({
        eventId,
        gameType: "lyrically-challenged",
        config,
      })
      trackEvent(analyticsEvents.lcGameCreated, {
        eventId,
        gameId,
        decadesEnabledCount: config.decadesEnabled.length,
        reopensEvent,
      })
      if (reopensEvent) onEventReopened?.()
      await navigate({
        to: "/host/games/$gameId",
        params: { gameId },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setCreating(false)
    }
  }

  const stagger = reduce ? 0 : 0.04
  const enterDuration = reduce ? 0 : 0.18

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Games in this event</h2>
          <p className="mt-1 text-xs text-[color:var(--muted-foreground)]">
            Run one live game at a time. Completed events can be reopened with a
            fresh game.
          </p>
        </div>
        {!eventCompleted && canCreateGame ? (
          <button
            type="button"
            onClick={handleCreateLC}
            disabled={creating}
            className="inline-flex items-center gap-1 rounded-md border border-[color:var(--border)] px-3 py-1 text-xs transition-colors duration-150 hover:bg-[color:var(--muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {creating ? "Creating…" : createLabel}
          </button>
        ) : null}
      </div>
      {eventCompleted ? (
        <section className="mb-4 rounded-xl border border-primary/25 bg-primary/[0.08] p-4 shadow-[0_20px_48px_-34px_rgba(67,65,201,0.55)]">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">
                Need another game?
              </p>
              <h3 className="mt-1 text-lg font-semibold text-[color:var(--foreground)]">
                Spin up the next Lyrically Challenged run.
              </h3>
              <p className="mt-1 text-sm leading-6 text-[color:var(--muted-foreground)]">
                Create a fresh game, then open its registration from the host console.
              </p>
            </div>
            <button
              type="button"
              onClick={handleCreateLC}
              disabled={creating}
              className="inline-flex shrink-0 items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-[0_18px_40px_-24px_rgba(67,65,201,0.95)] transition-all duration-150 hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {creating ? "Creating…" : "Create next game"}
            </button>
          </div>
        </section>
      ) : null}
      {!canCreateGame ? (
        <p className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          Canceled events cannot accept new games.
        </p>
      ) : null}
      <AnimatePresence>
        {error && (
          <motion.p
            key="err"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18 }}
            className="mb-2 rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-900"
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>
      {games === undefined ? (
        <ul className="divide-y divide-[color:var(--border)] rounded-md border border-[color:var(--border)]">
          {[0, 1].map((i) => (
            <li key={i} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="flex-1 space-y-2">
                <div className="h-3 w-1/3 animate-pulse rounded bg-[color:var(--muted)]" />
                <div className="h-2.5 w-1/4 animate-pulse rounded bg-[color:var(--muted)]" />
              </div>
              <div className="h-4 w-16 animate-pulse rounded-full bg-[color:var(--muted)]" />
            </li>
          ))}
        </ul>
      ) : visibleGames?.length === 0 ? (
        <div className="rounded-md border border-dashed border-[color:var(--border)] bg-[color:var(--muted)]/30 p-6 text-center">
          <p className="text-sm font-medium">No games yet</p>
          <p className="mt-1 text-xs text-[color:var(--muted-foreground)]">
            Add a game above to start collecting team registrations.
          </p>
        </div>
      ) : (
        <motion.ul
          className="divide-y divide-[color:var(--border)] overflow-hidden rounded-md border border-[color:var(--border)]"
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: stagger } },
          }}
        >
          {visibleGames?.map((game) => {
            const label =
              gameRegistry[game.gameType as keyof typeof gameRegistry]?.label ??
              game.gameType
            return (
              <motion.li
                key={game._id}
                variants={{
                  hidden: { opacity: 0, y: reduce ? 0 : 4 },
                  show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: enterDuration, ease: "easeOut" },
                  },
                }}
              >
                <div className="flex flex-col gap-3 px-4 py-3 text-sm transition-colors duration-150 hover:bg-[color:var(--muted)] sm:flex-row sm:items-center sm:justify-between">
                  <Link
                    to="/host/games/$gameId"
                    params={{ gameId: game._id }}
                    className="min-w-0 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    <p className="truncate font-medium">{label}</p>
                    <p className="font-mono text-xs text-[color:var(--muted-foreground)]">
                      {game.status}
                    </p>
                  </Link>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <GameStatusBadge status={game.status} />
                    <Link
                      to="/host/games/$gameId"
                      params={{ gameId: game._id }}
                      className="rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground shadow-[0_14px_32px_-22px_rgba(67,65,201,0.9)] transition-all duration-150 hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 active:scale-[0.98]"
                    >
                      Run game
                    </Link>
                  </div>
                </div>
              </motion.li>
            )
          })}
        </motion.ul>
      )}
    </div>
  )
}

function GameStatusBadge({ status }: { status: string }) {
  const tone: Record<string, string> = {
    scheduled:
      "border-[color:var(--border)] bg-[color:var(--background)] text-[color:var(--muted-foreground)]",
    ready: "border-amber-300 bg-amber-50 text-amber-800",
    active: "border-emerald-300 bg-emerald-50 text-emerald-800",
    completed:
      "border-[color:var(--border)] bg-[color:var(--muted)] text-[color:var(--muted-foreground)]",
    cancelled: "border-red-200 bg-red-50 text-red-700",
  }
  const cls =
    tone[status] ??
    "border-[color:var(--border)] bg-[color:var(--background)] text-[color:var(--muted-foreground)]"
  return (
    <span
      className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wide ${cls}`}
    >
      {status === "cancelled" ? "canceled" : status}
    </span>
  )
}

function Field({
  label,
  children,
  hint,
  invalid,
}: {
  label: string
  children: React.ReactNode
  hint?: string
  invalid?: boolean
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-[color:var(--muted-foreground)]">
        {label}
      </span>
      {children}
      <AnimatePresence>
        {hint && (
          <motion.span
            key={hint}
            initial={{ opacity: 0, y: -2 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -2 }}
            transition={{ duration: 0.16 }}
            className={`mt-1 block text-xs ${invalid ? "text-red-700" : "text-[color:var(--muted-foreground)]"}`}
          >
            {hint}
          </motion.span>
        )}
      </AnimatePresence>
    </label>
  )
}

function toDatetimeLocal(epochMs: number): string {
  const d = new Date(epochMs)
  const tzOffsetMs = d.getTimezoneOffset() * 60_000
  return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 16)
}
